import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PersonnelFileTab from "@/components/admin/instructors/personnel/PersonnelFileTab";
import { resolveAssignmentCompensation } from "@/lib/assignmentCompensation";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, AlertTriangle, ShieldCheck, ShieldAlert, ShieldOff, GitMerge } from "lucide-react";
import ImportInstructorFileButton from "@/components/admin/instructors/ImportInstructorFileButton";
import ImportHistoryTable from "@/components/admin/instructors/ImportHistoryTable";
import InstructorOnboardingPanel from "./InstructorOnboardingPanel";
import { checkInstructorReadiness, type ReadinessResult } from "@/lib/instructorReadiness";
import { getInstructorMissingRequirements } from "@/lib/instructorMissing";
import { useInstructorRealtime, upsertById, removeById } from "@/hooks/useInstructorRealtime";
import MergeDialog from "@/components/admin/instructors/MergeDialog";
import InstructorPickerDialog from "@/components/admin/instructors/InstructorPickerDialog";
import PossibleDuplicatesCard from "@/components/admin/instructors/PossibleDuplicatesCard";
import { usePublishPageContext } from "@/components/admin/eos/PageContext";


// Directory default: show the people you actually work with. Volunteers are
// working instructors too (unpaid), so they belong in the default view — they
// were previously hidden behind "All statuses".
const ACTIVE_PROSPECT = "active_prospect";
const ACTIVE_PROSPECT_SET = ["active", "approved", "prospect", "volunteer"];

// No test flag exists on instructor records, so test/demo rows are recognised
// by name/email pattern only. Display-only — nothing is ever modified.
const TEST_PATTERN = /(^|\b)(test|demo|sample|dummy)\b|@(example|test)\.(com|org)/i;
function isTestish(value: string | null | undefined): boolean {
  return !!value && TEST_PATTERN.test(value);
}

function humanizeStatus(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type InstructorStatus =
  | "prospect" | "approved" | "active" | "volunteer" | "on_hold" | "inactive" | "former" | "archived" | "community_partner" | "merged";


type PayRateType =
  | "hourly" | "flat_per_meeting" | "flat_per_semester" | "per_student" | "daily" | "custom";

type Instructor = {
  id: string;
  full_name: string;
  legal_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone: string | null;
  mailing_address: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  biography: string | null;
  experience: string | null;
  status: InstructorStatus;
  hourly_rate: number | null;
  pay_rate_type: PayRateType | null;
  pay_rate_amount: number | null;
  pay_rate_effective_date: string | null;
  pay_frequency: string | null;
  payment_method: string | null;
  zelle_name: string | null;
  zelle_contact: string | null;
  payment_notes: string | null;
  onboarding_status: string;
  action_required: string | null;
  seed_source: string | null;
  teaching_agreement_signed: boolean;
  teaching_agreement_signed_date: string | null;
  teaching_agreement_year: number | null;
  background_check_completed: boolean;
  background_check_date: string | null;
  background_check_year: number | null;
  standards_of_conduct_acknowledged: boolean;
  standards_of_conduct_date: string | null;
  admin_notes: string | null;
  independent_contractor_ack: boolean;
  independent_contractor_ack_date: string | null;
  independent_contractor_ack_source: string | null;
  w9_status: "Not Requested" | "Requested" | "Received" | "Needs Update" | "Exempt or Not Required";
  w9_requested_at: string | null;
  w9_received_at: string | null;
  w9_tax_year: number | null;
  w9_document_path: string | null;
  review_1099_status: "Not Reviewed" | "Likely Required" | "Not Required" | "Sent to Accountant" | "Form Issued";
  tax_year_1099: number | null;
  date_sent_to_accountant: string | null;
  date_1099_issued: string | null;
  contractor_notes: string | null;
  ready_to_teach_override: boolean;
  ready_to_teach_override_reason: string | null;
  ready_to_teach_override_by: string | null;
  ready_to_teach_override_at: string | null;
  merged_into_id: string | null;
  merged_at: string | null;
  merge_reason: string | null;
  updated_at: string;
};

type Assignment = {
  id: string;
  instructor_id: string;
  class_slug: string;
  class_title: string | null;
  session_label: string | null;
  status: "active" | "upcoming" | "completed" | "cancelled" | "on_hold";
  day_of_week: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  capacity: number | null;
  pay_rate_type: PayRateType | null;
  pay_rate_amount: number | null;
  pay_rate_effective_date: string | null;
  notes: string | null;
};

type UnmatchedRef = {
  id: string;
  name_found: string;
  source: string;
  related_class_slug: string | null;
  related_session_label: string | null;
  suggested_instructor_id: string | null;
  status: "open" | "linked" | "created" | "ignored" | "placeholder";
  resolution: string | null;
};

const STATUSES: InstructorStatus[] = ["prospect", "approved", "active", "volunteer", "on_hold", "inactive", "former", "archived"];
const W9_STATUSES = ["Not Requested", "Requested", "Received", "Needs Update", "Exempt or Not Required"] as const;
const REVIEW_1099_STATUSES = ["Not Reviewed", "Likely Required", "Not Required", "Sent to Accountant", "Form Issued"] as const;
const IC_SOURCES = ["Proposal Form", "Annual Agreement", "Manual Entry"] as const;
const PAY_RATE_TYPES: { value: PayRateType; label: string }[] = [
  { value: "hourly", label: "Hourly" },
  { value: "flat_per_meeting", label: "Flat per class meeting" },
  { value: "flat_per_semester", label: "Flat per semester" },
  { value: "per_student", label: "Per student" },
  { value: "daily", label: "Daily rate" },
  { value: "custom", label: "Custom arrangement" },
];

type BadgeTone = "green" | "yellow" | "red" | "gray";
const toneClass: Record<BadgeTone, string> = {
  green: "bg-green-100 text-green-800 border-green-200",
  yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  red: "bg-red-100 text-red-800 border-red-200",
  gray: "bg-muted text-muted-foreground border-border",
};
const TagBadge = ({ tone, children }: { tone: BadgeTone; children: React.ReactNode }) => (
  <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneClass[tone]}`}>{children}</span>
);
const NeedsInfo = () => <span className="text-xs text-muted-foreground italic">Needs Information</span>;

function w9Tone(s: Instructor["w9_status"]): BadgeTone {
  if (s === "Received") return "green";
  if (s === "Requested" || s === "Needs Update") return "yellow";
  if (s === "Exempt or Not Required") return "gray";
  return "red";
}

// Ready-to-Teach uses the shared checker so UI + assistant + edge stay in sync.
function readinessOf(i: Instructor, assignmentsForI: Assignment[]): ReadinessResult {
  const hasAssignment = assignmentsForI.some((a) =>
    ["active", "upcoming", "proposed"].includes(a.status),
  );
  return checkInstructorReadiness(i as any, { hasAssignment });
}
function readyToTeach(i: Instructor, assignmentsForI: Assignment[] = []): boolean {
  const r = readinessOf(i, assignmentsForI);
  return r.status !== "not_ready"; // ready OR override counts as ready
}

function formatPayArrangement(i: Instructor): string {
  const type = i.pay_rate_type;
  const amt = i.pay_rate_amount ?? i.hourly_rate;
  if (!type && !amt) return "";
  if (!type && amt) return `$${amt}/hr (legacy)`;
  const label = PAY_RATE_TYPES.find((p) => p.value === type)?.label ?? type;
  return amt ? `${label} — $${amt}` : String(label);
}

// Delegates to the shared service so the directory, filters, missing-info
// queue, drawer, dashboard alerts, and assistant all report the same list.
function computeMissing(i: Instructor, assignments: Assignment[]): string[] {
  const forI = assignments.filter((a) => a.instructor_id === i.id);
  return getInstructorMissingRequirements(i as any, forI as any);
}

function ReadinessBanner({
  instructor,
  assignments,
}: {
  instructor: Instructor;
  assignments: Assignment[];
}) {
  const readiness = readinessOf(instructor, assignments);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"add" | "remove">("add");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const jump = (id: string) => {
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async () => {
    if (mode === "add" && reason.trim().length < 20) {
      toast.error("Please provide a reason of at least 20 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes?.user?.id ?? null;
      const now = new Date().toISOString();
      const patch = mode === "add"
        ? {
            ready_to_teach_override: true,
            ready_to_teach_override_reason: reason.trim(),
            ready_to_teach_override_by: uid,
            ready_to_teach_override_at: now,
          }
        : {
            ready_to_teach_override: false,
            ready_to_teach_override_reason: null,
            ready_to_teach_override_by: null,
            ready_to_teach_override_at: null,
          };
      const { error } = await (supabase as any)
        .from("instructors").update(patch).eq("id", instructor.id);
      if (error) throw error;
      await (supabase as any).from("instructor_audit_log").insert({
        instructor_id: instructor.id,
        admin_user_id: uid,
        action: mode === "add" ? "ready_to_teach_override_add" : "ready_to_teach_override_remove",
        field: "ready_to_teach_override",
        previous_value: String(readiness.overridden),
        new_value: String(mode === "add"),
      });
      toast.success(mode === "add" ? "Override recorded" : "Override removed");
      setOpen(false);
      setReason("");
    } catch (e: any) {
      toast.error("Couldn't save that pay override. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const tone =
    readiness.status === "ready"
      ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-800 dark:text-emerald-300"
      : readiness.status === "override"
      ? "border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-300"
      : "border-destructive/40 bg-destructive/5 text-destructive";
  const Icon =
    readiness.status === "ready" ? ShieldCheck
      : readiness.status === "override" ? ShieldAlert : ShieldOff;

  return (
    <div className={`rounded-md border p-3 space-y-2 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <Icon className="h-4 w-4" />
          {readiness.statusLabel}
        </div>
        <div className="flex gap-2">
          {readiness.overridden && (
            <Button size="sm" variant="outline" onClick={() => { setMode("remove"); setOpen(true); }}>
              Remove Override
            </Button>
          )}
          {readiness.status === "not_ready" && (
            <Button size="sm" variant="outline" onClick={() => { setMode("add"); setOpen(true); }}>
              Override and Mark Ready
            </Button>
          )}
        </div>
      </div>
      {readiness.blockers.length > 0 && (
        <div className="text-xs space-y-1">
          <div className="font-medium">Missing:</div>
          <ul className="space-y-1">
            {readiness.blockers.map((b) => (
              <li key={b.key} className="flex items-center justify-between gap-2">
                <span>• {b.label}</span>
                <button
                  type="button"
                  onClick={() => jump(b.jumpTo)}
                  className="underline text-[11px] opacity-80 hover:opacity-100"
                >
                  Jump to section
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {readiness.overridden && readiness.overrideReason && (
        <div className="text-xs italic opacity-90">
          Override reason: {readiness.overrideReason}
          {readiness.overrideAt && (
            <> — {new Date(readiness.overrideAt).toLocaleString()}</>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {mode === "add" ? "Override and Mark Ready to Teach" : "Remove Readiness Override"}
            </DialogTitle>
            <DialogDescription>
              {mode === "add"
                ? "This does not mark any missing compliance items as complete. It only allows the instructor to appear as Ready with Admin Override."
                : "The instructor will revert to Not Ready if requirements are still missing."}
            </DialogDescription>
          </DialogHeader>
          {mode === "add" && (
            <div className="space-y-2 text-sm">
              <div className="rounded bg-muted/50 p-2 text-xs">
                <div className="font-medium mb-1">Missing requirements:</div>
                <ul className="list-disc pl-4">
                  {readiness.blockers.map((b) => <li key={b.key}>{b.label}</li>)}
                </ul>
              </div>
              <div>
                <Label>Reason (minimum 20 characters)</Label>
                <Textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this override justified?"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {reason.trim().length} / 20
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy}>
              {mode === "add" ? "Record Override" : "Remove Override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const InstructorsManagement = () => {
  const [rows, setRows] = useState<Instructor[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [unmatched, setUnmatched] = useState<UnmatchedRef[]>([]);
  const [loading, setLoading] = useState(true);
  // Allow deep links such as /admin/instructors?tab=directory&q=Jane%20Doe to
  // land on the person you just approved from a proposal. Stale test-record
  // deep links (?q=Test Instructor) are ignored so the page opens clean.
  const [search, setSearch] = useState(() => {
    try {
      // One-time cleanup of any legacy persisted directory filter.
      window.localStorage.removeItem("instructors.filters");
      window.localStorage.removeItem("instructorDirectoryFilters");
      const q = new URLSearchParams(window.location.search).get("q") ?? "";
      return isTestish(q) ? "" : q;
    } catch {
      return "";
    }
  });

  const [statusFilter, setStatusFilter] = useState<string>(ACTIVE_PROSPECT);
  const [missingFilter, setMissingFilter] = useState<string>("all");
  const [editing, setEditing] = useState<Instructor | null>(null);
  const [draft, setDraft] = useState<Partial<Instructor>>({});
  const [uploadingW9, setUploadingW9] = useState(false);
  const [showMerged, setShowMerged] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [mergePair, setMergePair] = useState<{ primary: string; duplicate: string } | null>(null);
  const [pickPrimaryFor, setPickPrimaryFor] = useState<string | null>(null); // duplicate id waiting for primary picker


  // Publish the currently-open instructor into MAVIS page context so the assistant
  // can resolve "him/her/them/this instructor" without extra questions.
  usePublishPageContext({
    selection: editing
      ? {
          kind: "instructor",
          id: editing.id,
          label: editing.full_name || editing.email || "Instructor",
          pronoun: "them",
          attributes: {
            status: editing.status,
            email: editing.email,
            w9_status: (editing as unknown as { w9_status?: string }).w9_status,
          },
        }
      : null,
    visible: [
      {
        kind: "instructors",
        count: rows.length,
        sample: rows.slice(0, 5).map((r) => ({ id: r.id, label: r.full_name || r.email || "Instructor" })),
      },
    ],
    filters: {
      search: search || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      missing: missingFilter !== "all" ? missingFilter : undefined,
    },
  });

  const fetchAll = async () => {
    setLoading(true);
    const [{ data: iData }, { data: aData }, { data: uData }] = await Promise.all([
      (supabase as any).from("instructors").select("*").order("status").order("full_name"),
      (supabase as any).from("instructor_class_assignments").select("*").order("start_time"),
      (supabase as any).from("instructor_unmatched_references").select("*").eq("status", "open"),
    ]);
    setRows((iData as Instructor[]) ?? []);
    setAssignments((aData as Assignment[]) ?? []);
    setUnmatched((uData as UnmatchedRef[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  // Realtime: incrementally update local caches so the directory, drawer,
  // summary cards, and Missing Info queue refresh without a full refetch.
  // A unique channel topic per mount avoids the Supabase duplicate-channel crash.
  useInstructorRealtime({
    onInstructor: ({ event, new: n, old: o }) => {
      if (event === "DELETE") setRows((rs) => removeById(rs, o.id));
      else setRows((rs) => upsertById(rs, n as Instructor));
      // Keep the currently-open drawer in sync too.
      setEditing((e) => (e && n && e.id === n.id ? { ...e, ...(n as Instructor) } : e));
    },
    onAssignment: ({ event, new: n, old: o }) => {
      if (event === "DELETE") setAssignments((as) => removeById(as, o.id));
      else setAssignments((as) => upsertById(as, n as Assignment));
    },
    onUnmatched: ({ event, new: n, old: o }) => {
      if (event === "DELETE") setUnmatched((us) => removeById(us, o.id));
      else if (n?.status === "open") setUnmatched((us) => upsertById(us, n as UnmatchedRef));
      else setUnmatched((us) => removeById(us, (n?.id ?? o?.id) as string));
    },
    // Aliases + one-time payments + contact info are read by child editors;
    // those child components subscribe themselves as needed. We still receive
    // the events here so future summary counters could react.
  });


  const assignmentsByInstructor = useMemo(() => {
    const map = new Map<string, Assignment[]>();
    assignments.forEach((a) => {
      const arr = map.get(a.instructor_id) ?? [];
      arr.push(a);
      map.set(a.instructor_id, arr);
    });
    return map;
  }, [assignments]);

  // Summary counts
  const counts = useMemo(() => {
    const c = {
      active: 0, prospect: 0, former: 0, missingPay: 0, missingAgreement: 0,
      needsBG: 0, needsW9: 0, readyToTeach: 0, actionRequired: 0,
    };
    rows.forEach((r) => {
      if (r.status === "active") c.active += 1;
      if (r.status === "prospect" || r.status === "approved") c.prospect += 1;
      if (r.status === "former" || r.status === "inactive" || r.status === "archived") c.former += 1;
      const active = r.status === "active" || r.status === "approved";
      if (active && !(r.pay_rate_amount || r.hourly_rate)) c.missingPay += 1;
      if (active && !r.teaching_agreement_signed) c.missingAgreement += 1;
      if (active && !r.background_check_completed) c.needsBG += 1;
      if (active && r.w9_status !== "Received" && r.w9_status !== "Exempt or Not Required") c.needsW9 += 1;
      if (readyToTeach(r, assignmentsByInstructor.get(r.id) ?? [])) c.readyToTeach += 1;
      if (computeMissing(r, assignments).length > 0) c.actionRequired += 1;
    });
    return c;
  }, [rows, assignments, assignmentsByInstructor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searchingTest = isTestish(q);
    return rows.filter((r) => {
      if (r.status === "merged" && !showMerged) return false;
      if (!showTest && !searchingTest && (isTestish(r.full_name) || isTestish(r.email))) return false;
      if (statusFilter === ACTIVE_PROSPECT) {
        if (!ACTIVE_PROSPECT_SET.includes(r.status)) return false;
      } else if (statusFilter !== "all" && r.status !== statusFilter) return false;

      if (missingFilter !== "all") {
        const missing = computeMissing(r, assignments);
        const assignForI = assignmentsByInstructor.get(r.id) ?? [];
        const ready = readinessOf(r, assignForI);
        const map: Record<string, (i: Instructor) => boolean> = {
          contact: (i) => !i.phone || !i.email,
          pay_rate: (i) => !(i.pay_rate_amount || i.hourly_rate),
          payment_method: (i) => !i.payment_method,
          w9: (i) => i.w9_status !== "Received" && i.w9_status !== "Exempt or Not Required",
          agreement: (i) => !i.teaching_agreement_signed,
          background_check: (i) => i.status !== "volunteer" && !i.background_check_completed,
          ready_to_teach: () => ready.status === "ready",
          not_ready: () => ready.status === "not_ready",
          ready_with_override: () => ready.status === "override",
          action_required: () => missing.length > 0,
        };
        if (missingFilter === "any_missing" && missing.length === 0) return false;
        else if (missingFilter !== "any_missing" && map[missingFilter] && !map[missingFilter](r)) return false;
      }
      if (!q) return true;
      const assignedSlugs = (assignmentsByInstructor.get(r.id) ?? []).map((a) => (a.class_title ?? a.class_slug).toLowerCase()).join(" ");
      return `${r.full_name} ${r.preferred_name ?? ""} ${r.email ?? ""} ${r.phone ?? ""} ${assignedSlugs}`
        .toLowerCase().includes(q);
    });
  }, [rows, assignments, assignmentsByInstructor, search, statusFilter, missingFilter, showMerged, showTest]);

  const openEdit = (r: Instructor) => { setEditing(r); setDraft({ ...r }); };

  const save = async () => {
    if (!editing) return;
    const tracked: (keyof Instructor)[] = [
      "full_name", "legal_name", "preferred_name", "email", "phone", "mailing_address",
      "emergency_contact_name", "emergency_contact_phone", "status",
      "pay_rate_type", "pay_rate_amount", "pay_rate_effective_date", "pay_frequency",
      "hourly_rate", "payment_method", "zelle_name", "zelle_contact",
      "teaching_agreement_signed", "teaching_agreement_signed_date", "teaching_agreement_year",
      "background_check_completed", "background_check_date", "background_check_year",
      // Standards of Conduct is folded into the Teaching Agreement — no longer
      // tracked as a separate active requirement. Legacy field preserved for
      // historical audit but not surfaced in the edit dialog.
      "independent_contractor_ack", "independent_contractor_ack_source",
      "w9_status", "w9_tax_year", "w9_document_path",
      "review_1099_status", "tax_year_1099",
      "action_required", "onboarding_status",
    ];
    const changes = tracked
      .filter((k) => (editing as any)[k] !== (draft as any)[k])
      .map((k) => ({ field: k as string, previous: (editing as any)[k], next: (draft as any)[k] }));

    const { error } = await (supabase as any).from("instructors").update(draft).eq("id", editing.id);
    if (error) { toast.error(error.message); return; }
    if (changes.length) {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes?.user?.id ?? null;
      await (supabase as any).from("instructor_audit_log").insert(
        changes.map((c) => ({
          instructor_id: editing.id,
          admin_user_id: uid,
          action: "update",
          field: c.field,
          previous_value: c.previous == null ? null : String(c.previous),
          new_value: c.next == null ? null : String(c.next),
        })),
      );
    }
    toast.success("Instructor saved");
    // Optimistic merge — realtime UPDATE will confirm shortly (dedupe by id).
    setRows((rs) => upsertById(rs, { ...(editing as Instructor), ...(draft as Instructor) }));
    setEditing(null);
  };

  const uploadW9 = async (file: File) => {
    if (!editing) return;
    setUploadingW9(true);
    try {
      const path = `${editing.id}/${Date.now()}-${file.name.replace(/[^\w.-]/g, "_")}`;
      const { error } = await supabase.storage.from("w9-documents").upload(path, file, { upsert: false });
      if (error) throw error;
      setDraft((d) => ({ ...d, w9_document_path: path, w9_status: "Received", w9_received_at: new Date().toISOString().slice(0, 10) }));
      const { data: userRes } = await supabase.auth.getUser();
      await (supabase as any).from("instructor_audit_log").insert({
        instructor_id: editing.id,
        admin_user_id: userRes?.user?.id ?? null,
        action: "w9_document_uploaded",
        field: "w9_document_path",
        previous_value: editing.w9_document_path ?? null,
        new_value: path,
      });
      toast.success("W-9 uploaded securely");
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setUploadingW9(false);
    }
  };

  const downloadW9 = async () => {
    if (!draft.w9_document_path) return;
    const { data, error } = await supabase.storage.from("w9-documents").createSignedUrl(draft.w9_document_path, 300);
    if (error || !data?.signedUrl) { toast.error("Could not create signed URL"); return; }
    if (editing) {
      const { data: userRes } = await supabase.auth.getUser();
      await (supabase as any).from("instructor_audit_log").insert({
        instructor_id: editing.id,
        admin_user_id: userRes?.user?.id ?? null,
        action: "w9_document_accessed",
        field: "w9_document_path",
        new_value: draft.w9_document_path,
      });
    }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  const missingInfoList = useMemo(() =>
    rows
      .map((r) => ({ instructor: r, missing: computeMissing(r, assignments) }))
      .filter((x) => x.missing.length > 0 && (x.instructor.status === "active" || x.instructor.status === "approved" || x.instructor.status === "prospect")),
    [rows, assignments]);

  const SummaryCard = ({ label, value, onClick, tone }: { label: string; value: number; onClick?: () => void; tone?: BadgeTone }) => (
    <button
      onClick={onClick}
      className="text-left rounded-lg border bg-card p-3 hover:bg-muted/50 transition"
    >
      <div className={`text-2xl font-semibold ${tone === "red" ? "text-red-600" : tone === "green" ? "text-green-700" : "text-foreground"}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        <SummaryCard label="Active Instructors" value={counts.active} tone="green" onClick={() => { setStatusFilter("active"); setMissingFilter("all"); }} />
        <SummaryCard label="Prospective" value={counts.prospect} onClick={() => { setStatusFilter("prospect"); setMissingFilter("all"); }} />
        <SummaryCard label="Former" value={counts.former} onClick={() => { setStatusFilter("former"); setMissingFilter("all"); }} />
        <SummaryCard label="Ready to Teach" value={counts.readyToTeach} tone="green" onClick={() => { setStatusFilter("all"); setMissingFilter("ready_to_teach"); }} />
        <SummaryCard label="Action Required" value={counts.actionRequired} tone="red" onClick={() => { setStatusFilter("all"); setMissingFilter("action_required"); }} />
        <SummaryCard label="Missing Pay Info" value={counts.missingPay} tone="red" onClick={() => { setStatusFilter("all"); setMissingFilter("pay_rate"); }} />
        <SummaryCard label="Missing Agreements" value={counts.missingAgreement} tone="red" onClick={() => { setStatusFilter("all"); setMissingFilter("agreement"); }} />
        <SummaryCard label="Background Checks Needed" value={counts.needsBG} tone="red" onClick={() => { setStatusFilter("all"); setMissingFilter("background_check"); }} />
        <SummaryCard label="W-9s Needed" value={counts.needsW9} tone="red" onClick={() => { setStatusFilter("all"); setMissingFilter("w9"); }} />
      </div>

      <PossibleDuplicatesCard />


      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
            <span>Instructor Directory</span>
            <div className="flex items-center gap-2">
              <ImportInstructorFileButton onApplied={() => window.location.reload()} />
              <label className="flex items-center gap-1 text-xs text-muted-foreground font-normal">
                <Switch checked={showMerged} onCheckedChange={setShowMerged} /> Show merged
              </label>
              <label className="flex items-center gap-1 text-xs text-muted-foreground font-normal">
                <Switch checked={showTest} onCheckedChange={setShowTest} /> Show test records
              </label>
              <span className="text-xs text-muted-foreground font-normal">{filtered.length} shown of {rows.length} total</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name, class, phone, or email..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="md:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ACTIVE_PROSPECT}>Active + Volunteer + Prospect</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{humanizeStatus(s)}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={missingFilter} onValueChange={setMissingFilter}>
              <SelectTrigger className="md:w-56"><SelectValue placeholder="All records" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="ready_to_teach">Ready to Teach</SelectItem>
                <SelectItem value="ready_with_override">Ready with Override</SelectItem>
                <SelectItem value="not_ready">Not Ready</SelectItem>
                <SelectItem value="action_required">Action Required</SelectItem>
                <SelectItem value="any_missing">Any missing info</SelectItem>
                <SelectItem value="contact">Missing contact info</SelectItem>
                <SelectItem value="pay_rate">Missing pay rate</SelectItem>
                <SelectItem value="payment_method">Missing payment method</SelectItem>
                <SelectItem value="w9">Missing W-9</SelectItem>
                <SelectItem value="agreement">Missing agreement</SelectItem>
                <SelectItem value="background_check">Missing background check</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Instructor</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Current Class(es)</TableHead>
                <TableHead>Day / Time</TableHead>
                <TableHead>Pay Arrangement</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Agreement</TableHead>
                <TableHead>BG Check</TableHead>
                <TableHead>W-9</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Onboarding</TableHead>
                <TableHead>Action Required</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground">No instructors match the current filters.</TableCell></TableRow>
                ) : filtered.map((r) => {
                  const list = assignmentsByInstructor.get(r.id) ?? [];
                  const active = list.filter((a) => a.status === "active" || a.status === "upcoming");
                  const pay = formatPayArrangement(r);
                  const ready = readyToTeach(r, list);
                  const readyDetail = readinessOf(r, list);
                  return (
                    <TableRow key={r.id} className={ready ? "bg-green-50/40" : ""}>
                      <TableCell className="font-medium">
                        <div>{r.full_name}</div>
                        {r.preferred_name && r.preferred_name !== r.full_name && <div className="text-xs text-muted-foreground">Preferred: {r.preferred_name}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{r.phone ?? <NeedsInfo />}</div>
                        <div className="text-muted-foreground">{r.email ?? <NeedsInfo />}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {active.length === 0 ? <span className="text-muted-foreground">—</span> : active.slice(0, 3).map((a) => (
                          <div key={a.id}>
                            {a.class_title ?? a.class_slug} <span className="text-muted-foreground">({a.session_label ?? "—"})</span>
                          </div>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs">
                        {active.length === 0 ? <span className="text-muted-foreground">—</span> : active.slice(0, 3).map((a) => (
                          <div key={a.id}>{a.day_of_week ?? "—"} {a.start_time ? `${a.start_time}–${a.end_time ?? ""}` : ""}</div>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs">{pay || <NeedsInfo />}</TableCell>
                      <TableCell className="text-xs">
                        <div>{r.payment_method ?? <NeedsInfo />}</div>
                        {r.payment_method?.toLowerCase().includes("zelle") && (
                          <div className="text-muted-foreground">{r.zelle_contact ? "Zelle on file" : <span className="text-red-600">Zelle info missing</span>}</div>
                        )}
                      </TableCell>
                      <TableCell><TagBadge tone={r.teaching_agreement_signed ? "green" : "red"}>{r.teaching_agreement_signed ? (r.teaching_agreement_year ? `Signed ${r.teaching_agreement_year}` : "Signed") : "Agreement missing"}</TagBadge></TableCell>
                      <TableCell>
                        <TagBadge tone={r.background_check_completed ? "green" : "red"}>{r.background_check_completed ? (r.background_check_year ? `Cleared ${r.background_check_year}` : "Cleared") : "Check needed"}</TagBadge>
                      </TableCell>
                      <TableCell><TagBadge tone={w9Tone(r.w9_status)}>{humanizeStatus(r.w9_status)}</TagBadge></TableCell>
                      <TableCell><Badge variant="outline">{humanizeStatus(r.status)}</Badge></TableCell>
                      <TableCell className="text-xs">{humanizeStatus(r.onboarding_status)}</TableCell>
                      <TableCell className="text-xs min-w-[220px] max-w-[320px] whitespace-normal">
                        {(() => {
                          const missing = computeMissing(r, assignments);
                          if (missing.length === 0) {
                            return <span className="text-muted-foreground">No action required</span>;
                          }
                          const text = `Needs ${missing.join(", ")}`;
                          return <span className="text-red-700 leading-relaxed">{text}</span>;
                        })()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => openEdit(r)}>View profile</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Missing Information queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            Instructor Information Needed
            <span className="text-xs text-muted-foreground font-normal">({missingInfoList.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {missingInfoList.length === 0 ? (
            <div className="text-sm text-muted-foreground">All active instructors have their required information on file.</div>
          ) : (
            <ul className="divide-y">
              {missingInfoList.map(({ instructor, missing }) => (
                <li key={instructor.id} className="py-2 flex items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-medium">{instructor.preferred_name ?? instructor.full_name}</span>{" "}
                    <span className="text-xs text-muted-foreground">({instructor.status})</span>
                    <div className="text-xs text-muted-foreground">Missing: {missing.join(", ")}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openEdit(instructor)}>Enter info</Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Unmatched references */}
      {unmatched.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Unmatched Instructor References ({unmatched.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <UnmatchedRefsList
              refs={unmatched}
              instructors={rows}
              onChanged={fetchAll}
            />
          </CardContent>
        </Card>
      )}

      <ImportHistoryTable />




      {/* Edit / profile dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-2 pr-6">
                  <div>
                    {editing.preferred_name ?? editing.full_name}
                    <span className="text-xs font-normal text-muted-foreground ml-2">({editing.status})</span>
                  </div>
                  {editing.status !== "merged" && (
                    <Button size="sm" variant="outline" onClick={() => setPickPrimaryFor(editing.id)}>
                      <GitMerge className="h-3.5 w-3.5 mr-1" /> Merge into another…
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>
              {editing.status === "merged" && editing.merged_into_id && (
                <div className="rounded border border-amber-500/40 bg-amber-50/40 p-2 text-xs">
                  This record was merged into{" "}
                  <button
                    className="underline font-medium"
                    onClick={() => {
                      const target = rows.find((r) => r.id === editing.merged_into_id);
                      if (target) openEdit(target);
                    }}
                  >
                    {rows.find((r) => r.id === editing.merged_into_id)?.full_name ?? "another instructor"}
                  </button>
                  {editing.merged_at ? ` on ${new Date(editing.merged_at).toLocaleDateString()}` : ""}.
                  {editing.merge_reason ? ` Reason: ${editing.merge_reason}` : ""}
                </div>
              )}
              <Tabs defaultValue="profile" className="mt-2">
                <TabsList>
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="personnel">Personnel File</TabsTrigger>
                </TabsList>
                <TabsContent value="profile" className="mt-3">
                  <div className="space-y-4 text-sm">
                    <ReadinessBanner
                      instructor={editing}
                      assignments={assignmentsByInstructor.get(editing.id) ?? []}
                    />
                    {/* Contact */}
                    <section id="section-contact" className="space-y-3 scroll-mt-4">
                      <h3 className="font-semibold">Contact Information</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Full name (display)</Label><Input value={draft.full_name ?? ""} onChange={(e) => setDraft({ ...draft, full_name: e.target.value })} /></div>
                        <div><Label>Legal name</Label><Input value={draft.legal_name ?? ""} onChange={(e) => setDraft({ ...draft, legal_name: e.target.value })} placeholder="If different from display name" /></div>
                        <div><Label>Preferred name</Label><Input value={draft.preferred_name ?? ""} onChange={(e) => setDraft({ ...draft, preferred_name: e.target.value })} /></div>
                        <div>
                          <Label>Status</Label>
                          <Select value={draft.status ?? "prospect"} onValueChange={(v) => setDraft({ ...draft, status: v as InstructorStatus })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Email</Label><Input value={draft.email ?? ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></div>
                        <div><Label>Phone</Label><Input value={draft.phone ?? ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></div>
                        <div className="col-span-2"><Label>Mailing address (optional)</Label><Input value={draft.mailing_address ?? ""} onChange={(e) => setDraft({ ...draft, mailing_address: e.target.value })} /></div>
                        <div><Label>Emergency contact name</Label><Input value={draft.emergency_contact_name ?? ""} onChange={(e) => setDraft({ ...draft, emergency_contact_name: e.target.value })} /></div>
                        <div><Label>Emergency contact phone</Label><Input value={draft.emergency_contact_phone ?? ""} onChange={(e) => setDraft({ ...draft, emergency_contact_phone: e.target.value })} /></div>
                      </div>
                    </section>

                    {/* Class Assignments (read-only mirror of the canonical rows) */}
                    <section id="section-assignments" className="space-y-2 border-t pt-3 scroll-mt-4">
                      <AssignmentsEditor
                        instructorId={editing.id}
                        assignments={assignmentsByInstructor.get(editing.id) ?? []}
                      />
                    </section>

                    {/* Aliases */}
                    <section className="border-t pt-3">
                      <AliasesEditor instructorId={editing.id} />
                    </section>


                    <div><Label>Biography</Label><Textarea rows={3} value={draft.biography ?? ""} onChange={(e) => setDraft({ ...draft, biography: e.target.value })} /></div>
                    <div><Label>Experience / Qualifications</Label><Textarea rows={3} value={draft.experience ?? ""} onChange={(e) => setDraft({ ...draft, experience: e.target.value })} /></div>

                    {/* Pay Arrangement */}
                    <section id="section-pay" className="border-t pt-3 space-y-3 scroll-mt-4">
                      <h3 className="font-semibold">Pay Arrangement <span className="text-xs font-normal text-muted-foreground">(admin-only)</span></h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Rate type</Label>
                          <Select value={draft.pay_rate_type ?? ""} onValueChange={(v) => setDraft({ ...draft, pay_rate_type: (v || null) as PayRateType | null })}>
                            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>{PAY_RATE_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Rate amount ($)</Label><Input type="number" step="0.01" value={draft.pay_rate_amount ?? ""} onChange={(e) => setDraft({ ...draft, pay_rate_amount: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                        <div><Label>Effective date</Label><Input type="date" value={draft.pay_rate_effective_date ?? ""} onChange={(e) => setDraft({ ...draft, pay_rate_effective_date: e.target.value || null })} /></div>
                        <div><Label>Pay frequency</Label><Input value={draft.pay_frequency ?? ""} onChange={(e) => setDraft({ ...draft, pay_frequency: e.target.value })} placeholder="e.g. per session, monthly" /></div>
                        <div><Label>Payment method</Label><Input value={draft.payment_method ?? ""} onChange={(e) => setDraft({ ...draft, payment_method: e.target.value })} placeholder="Zelle, Check, Cash…" /></div>
                        <div><Label>Legacy hourly rate</Label><Input type="number" step="0.01" value={draft.hourly_rate ?? ""} onChange={(e) => setDraft({ ...draft, hourly_rate: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                        <div><Label>Zelle name</Label><Input value={draft.zelle_name ?? ""} onChange={(e) => setDraft({ ...draft, zelle_name: e.target.value })} /></div>
                        <div><Label>Zelle phone / email</Label><Input value={draft.zelle_contact ?? ""} onChange={(e) => setDraft({ ...draft, zelle_contact: e.target.value })} /></div>
                      </div>
                      <div><Label>Payment notes</Label><Textarea rows={2} value={draft.payment_notes ?? ""} onChange={(e) => setDraft({ ...draft, payment_notes: e.target.value })} /></div>
                    </section>

                    {/* Compliance */}
                    <section id="section-compliance" className="border-t pt-3 space-y-3 scroll-mt-4">
                      <h3 className="font-semibold">Annual Compliance</h3>
                      <div className="grid grid-cols-3 gap-3 items-end">
                        <label className="flex items-center gap-2"><Switch checked={!!draft.teaching_agreement_signed} onCheckedChange={(v) => setDraft({ ...draft, teaching_agreement_signed: v })} /> Teaching Agreement</label>
                        <div><Label>Date signed</Label><Input type="date" value={draft.teaching_agreement_signed_date ?? ""} onChange={(e) => setDraft({ ...draft, teaching_agreement_signed_date: e.target.value || null })} /></div>
                        <div><Label>Year</Label><Input type="number" value={draft.teaching_agreement_year ?? ""} onChange={(e) => setDraft({ ...draft, teaching_agreement_year: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 items-end">
                        <label className="flex items-center gap-2"><Switch checked={!!draft.background_check_completed} onCheckedChange={(v) => setDraft({ ...draft, background_check_completed: v })} /> Background Check</label>
                        <div><Label>Date</Label><Input type="date" value={draft.background_check_date ?? ""} onChange={(e) => setDraft({ ...draft, background_check_date: e.target.value || null })} /></div>
                        <div><Label>Year</Label><Input type="number" value={draft.background_check_year ?? ""} onChange={(e) => setDraft({ ...draft, background_check_year: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Standards of Conduct is included in the Teaching Agreement and is
                        no longer tracked as a separate requirement.
                      </p>

                    </section>

                    {/* Contractor & Tax */}
                    <section id="section-contractor" className="border-t pt-3 space-y-3 scroll-mt-4">
                      <h3 className="font-semibold">Tax Records <span className="text-xs font-normal text-muted-foreground">(admin-only)</span></h3>
                      <p className="text-xs text-muted-foreground">
                        Independent Contractor Agreement is no longer tracked separately —
                        the Teaching Agreement is the governing document for instructors.
                      </p>
                      <div className="grid grid-cols-4 gap-3 items-end">
                        <div>
                          <Label>W-9 Status</Label>
                          <Select value={draft.w9_status ?? "Not Requested"} onValueChange={(v) => setDraft({ ...draft, w9_status: v as Instructor["w9_status"] })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{W9_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Requested</Label><Input type="date" value={draft.w9_requested_at ?? ""} onChange={(e) => setDraft({ ...draft, w9_requested_at: e.target.value || null })} /></div>
                        <div><Label>Received</Label><Input type="date" value={draft.w9_received_at ?? ""} onChange={(e) => setDraft({ ...draft, w9_received_at: e.target.value || null })} /></div>
                        <div><Label>W-9 Tax Year</Label><Input type="number" value={draft.w9_tax_year ?? ""} onChange={(e) => setDraft({ ...draft, w9_tax_year: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                      </div>
                      <div className="rounded-md border p-3 bg-muted/30">
                        <Label className="text-xs">Secure W-9 document (private storage, admin-only access)</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <Input type="file" accept="application/pdf,image/*" disabled={uploadingW9}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadW9(f); }} />
                          {draft.w9_document_path && <Button type="button" size="sm" variant="outline" onClick={downloadW9}>View</Button>}
                        </div>
                        {draft.w9_document_path && <p className="text-xs text-muted-foreground mt-1 truncate">On file: {draft.w9_document_path}</p>}
                        <p className="text-[11px] text-muted-foreground mt-1">Do not paste taxpayer IDs into notes or public fields.</p>
                      </div>
                      <div className="grid grid-cols-4 gap-3 items-end">
                        <div>
                          <Label>1099 Review Status</Label>
                          <Select value={draft.review_1099_status ?? "Not Reviewed"} onValueChange={(v) => setDraft({ ...draft, review_1099_status: v as Instructor["review_1099_status"] })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{REVIEW_1099_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>1099 Tax Year</Label><Input type="number" value={draft.tax_year_1099 ?? ""} onChange={(e) => setDraft({ ...draft, tax_year_1099: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                        <div><Label>Sent to Accountant</Label><Input type="date" value={draft.date_sent_to_accountant ?? ""} onChange={(e) => setDraft({ ...draft, date_sent_to_accountant: e.target.value || null })} /></div>
                        <div><Label>Form Issued</Label><Input type="date" value={draft.date_1099_issued ?? ""} onChange={(e) => setDraft({ ...draft, date_1099_issued: e.target.value || null })} /></div>
                      </div>
                      <div>
                        <Label>Contractor Notes</Label>
                        <Textarea rows={3} value={draft.contractor_notes ?? ""} onChange={(e) => setDraft({ ...draft, contractor_notes: e.target.value })} />
                        <p className="text-[11px] text-muted-foreground mt-1">Never store SSNs, EINs, or taxpayer identification numbers here.</p>
                      </div>
                    </section>

                    {/* Action Required + admin notes */}
                    <section className="border-t pt-3 space-y-3">
                      <div>
                        <Label>Action Required</Label>
                        <div className="rounded border bg-muted/40 px-3 py-2 text-xs">
                          {(() => {
                            const missing = computeMissing(editing, assignments);
                            if (missing.length === 0) return <span className="text-muted-foreground">No action required — all requirements met.</span>;
                            return <span className="text-red-700">Needs {missing.join(", ")}</span>;
                          })()}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Computed live from instructor fields. The legacy free-text field is deprecated and no longer displayed anywhere; update the underlying fields (phone, pay, W-9, etc.) to clear items.
                        </p>
                      </div>
                      <div><Label>Admin notes</Label><Textarea rows={3} value={draft.admin_notes ?? ""} onChange={(e) => setDraft({ ...draft, admin_notes: e.target.value })} /></div>
                    </section>

                    <div id="section-onboarding" className="border-t pt-3 scroll-mt-4">
                      <InstructorOnboardingPanel instructorId={editing.id} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="personnel" className="mt-3">
                  <PersonnelFileTab instructor={editing} />
                </TabsContent>
              </Tabs>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save}>Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <InstructorPickerDialog
        open={!!pickPrimaryFor}
        onOpenChange={(o) => !o && setPickPrimaryFor(null)}
        excludeId={pickPrimaryFor}
        title="Select the record to KEEP (primary)"
        onPick={(primaryId) => {
          if (pickPrimaryFor) setMergePair({ primary: primaryId, duplicate: pickPrimaryFor });
          setPickPrimaryFor(null);
        }}
      />
      <MergeDialog
        open={!!mergePair}
        onOpenChange={(o) => !o && setMergePair(null)}
        primaryId={mergePair?.primary ?? null}
        duplicateId={mergePair?.duplicate ?? null}
        onMerged={() => { setMergePair(null); fetchAll(); setEditing(null); }}
      />
    </div>
  );
};

export default InstructorsManagement;

// ---------- Assignment History (READ-ONLY) ----------
// The instructor card is a MIRROR of the canonical rows in
// public.instructor_class_assignments. All writes happen class-side (Class
// Editor → Instructor tab, or the Staffing Board) through src/lib/staffing.ts
// so both directions of the relationship always agree and history is never
// deleted.
type AssignmentRow = {
  id: string;
  class_slug: string | null;
  class_title: string | null;
  session_label: string | null;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
  day_of_week: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  capacity: number | null;
  pay_rate_type: string | null;
  pay_rate_amount: number | null;
  notes: string | null;
};

const LIVE_STATUSES = ["active", "upcoming", "future", "temporary"];

const AssignmentsEditor = ({
  instructorId,
  assignments,
}: {
  instructorId: string;
  assignments: AssignmentRow[];
}) => {
  // Compensation configs payroll falls back to when an assignment carries no
  // mode of its own — loaded so the card shows the rate payroll will REALLY
  // use instead of "Compensation not set".
  const [configs, setConfigs] = useState<any[]>([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await (supabase.from as any)("instructor_compensation_config")
        .select("class_slug, compensation_source, rate_cents, rate_model, effective_start_date, effective_end_date")
        .eq("instructor_id", instructorId);
      if (alive) setConfigs(data ?? []);
    })();
    return () => { alive = false; };
  }, [instructorId]);
  const configFor = (slug: string | null) => {
    const rows = configs.filter((c) => c.class_slug === slug);
    const wide = configs.filter((c) => !c.class_slug);
    const c = rows[0] ?? wide[0];
    if (!c) return null;
    return {
      compensation_source: c.compensation_source,
      rate_cents: c.rate_cents,
      rate_model: c.rate_model,
      instructorWide: !c.class_slug,
    };
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  const isLive = (a: AssignmentRow) =>
    LIVE_STATUSES.includes((a.status ?? "").toLowerCase()) &&
    (!a.end_date || a.end_date >= todayIso);
  const current = assignments.filter(isLive);
  const past = assignments.filter((a) => !isLive(a));

  const renderRow = (a: AssignmentRow) => (
    <li key={a.id} className="border rounded p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">
          {a.class_title ?? a.class_slug}{" "}
          <span className="text-muted-foreground">— {a.session_label ?? ""}</span>{" "}
          <Badge variant="outline" className="ml-1">{a.status}</Badge>
        </div>
        <div className="text-muted-foreground">
          {a.start_date ?? "—"} → {a.end_date ?? "open"}
        </div>
      </div>
      <div className="text-muted-foreground">
        {a.day_of_week ?? "—"} {a.start_time ? `${a.start_time}–${a.end_time ?? ""}` : ""} ·{" "}
        {a.location ?? "Location TBD"} · Cap {a.capacity ?? "—"}
      </div>
      <div className="text-muted-foreground">
        Compensation: {resolveAssignmentCompensation(a as any, configFor(a.class_slug ?? null)).label}
      </div>
      {a.notes && <div className="italic">{a.notes}</div>}
    </li>
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Class Assignments</h3>
        <Button size="sm" variant="outline" asChild>
          <a href="/admin/staffing">Manage on Staffing Board</a>
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Read-only history. Assignments are created, changed, and ended from the class side so
        payroll, the public site, and the admin cards stay in sync.
      </p>

      {assignments.length === 0 ? (
        <p className="text-xs text-muted-foreground">No assignments linked.</p>
      ) : (
        <div className="space-y-2 text-xs">
          <div>
            <div className="font-medium mb-1">Current & upcoming</div>
            {current.length === 0 ? (
              <p className="text-muted-foreground">None.</p>
            ) : (
              <ul className="space-y-1">{current.map(renderRow)}</ul>
            )}
          </div>
          {past.length > 0 && (
            <div>
              <div className="font-medium mb-1">Past / closed</div>
              <ul className="space-y-1 opacity-80">{past.map(renderRow)}</ul>
            </div>
          )}
        </div>
      )}
    </>
  );
};

// ---------- Aliases Editor ----------
const AliasesEditor = ({ instructorId }: { instructorId: string }) => {
  const [aliases, setAliases] = useState<{ id: string; alias: string; source: string | null }[]>([]);
  const [newAlias, setNewAlias] = useState("");

  const load = async () => {
    const { data } = await supabase.from("instructor_aliases").select("id, alias, source").eq("instructor_id", instructorId).order("alias");
    setAliases((data ?? []) as any);
  };
  useEffect(() => { load(); }, [instructorId]);

  const add = async () => {
    if (!newAlias.trim()) return;
    const { error } = await supabase.from("instructor_aliases").insert({ instructor_id: instructorId, alias: newAlias.trim(), source: "admin" });
    if (error) { toast.error(error.message); return; }
    setNewAlias(""); load();
  };
  const remove = async (id: string) => {
    if (!confirm("Delete alias?")) return;
    const { error } = await supabase.from("instructor_aliases").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    load();
  };

  return (
    <div className="space-y-2">
      <h3 className="font-semibold">Aliases / Nicknames</h3>
      <div className="flex flex-wrap gap-1">
        {aliases.map((a) => (
          <Badge key={a.id} variant="secondary" className="cursor-pointer" onClick={() => remove(a.id)} title="Click to remove">
            {a.alias} ×
          </Badge>
        ))}
        {aliases.length === 0 && <span className="text-xs text-muted-foreground">No aliases yet.</span>}
      </div>
      <div className="flex gap-1">
        <Input placeholder="Add alias (e.g. Miss Nancy)" value={newAlias} onChange={(e) => setNewAlias(e.target.value)} className="h-8 text-xs" />
        <Button size="sm" onClick={add}>Add</Button>
      </div>
    </div>
  );
};

// ---------- Unmatched Refs List ----------
const UnmatchedRefsList = ({
  refs,
  instructors,
  onChanged,
}: {
  refs: any[];
  instructors: any[];
  onChanged: () => void;
}) => {
  const [linkTarget, setLinkTarget] = useState<Record<string, string>>({});

  const link = async (refId: string) => {
    const instructorId = linkTarget[refId];
    if (!instructorId) { toast.error("Choose an instructor"); return; }
    const ref = refs.find((r) => r.id === refId);
    const { error } = await supabase.from("instructor_unmatched_references").update({
      status: "linked", suggested_instructor_id: instructorId, resolution: `Linked to instructor ${instructorId}`,
    }).eq("id", refId);
    if (error) { toast.error(error.message); return; }
    if (ref?.name_found) {
      await supabase.from("instructor_aliases").insert({ instructor_id: instructorId, alias: ref.name_found, source: "unmatched_link" });
    }
    toast.success("Linked and alias added");
    onChanged();
  };
  const ignore = async (refId: string) => {
    const { error } = await supabase.from("instructor_unmatched_references").update({ status: "ignored", resolution: "Ignored" }).eq("id", refId);
    if (error) { toast.error(error.message); return; }
    toast.success("Ignored");
    onChanged();
  };
  const createFrom = async (refId: string) => {
    const ref = refs.find((r) => r.id === refId);
    if (!ref?.name_found) return;
    if (!confirm(`Create new instructor profile for "${ref.name_found}"?`)) return;
    const { data: newInstr, error } = await supabase.from("instructors").insert({
      full_name: ref.name_found, status: "prospect", seed_source: "admin_from_unmatched",
    }).select("id").single();
    if (error) { toast.error(error.message); return; }
    await supabase.from("instructor_unmatched_references").update({
      status: "created", suggested_instructor_id: newInstr!.id, resolution: `Created instructor ${newInstr!.id}`,
    }).eq("id", refId);
    toast.success("Instructor created");
    onChanged();
  };

  return (
    <ul className="divide-y text-sm">
      {refs.map((u) => (
        <li key={u.id} className="py-2 space-y-2">
          <div>
            <div className="font-medium">{u.name_found}</div>
            <div className="text-xs text-muted-foreground">Source: {u.source} {u.related_class_slug ? `· Class: ${u.related_class_slug}` : ""}</div>
            {u.resolution && <div className="text-xs mt-1 italic">{u.resolution}</div>}
          </div>
          {u.status === "open" && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="border rounded px-2 py-1 text-xs"
                value={linkTarget[u.id] ?? ""}
                onChange={(e) => setLinkTarget({ ...linkTarget, [u.id]: e.target.value })}
              >
                <option value="">— link to existing —</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>{i.preferred_name || i.full_name}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={() => link(u.id)}>Link</Button>
              <Button size="sm" variant="outline" onClick={() => createFrom(u.id)}>Create New</Button>
              <Button size="sm" variant="ghost" onClick={() => ignore(u.id)}>Ignore</Button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

